import { Loader2, Mail, TrendingUp } from "lucide-react";

import { Chip, DrawerShell, ProvenanceBadge } from "@/components/comms";
import { type NudgeHistoryItem, useShareNudges } from "@/lib/comms";
import { cn, formatShortDateTime } from "@/lib/utils";

interface NudgeHistoryDrawerProps {
  accountId: string;
  accountName: string;
  open: boolean;
  onClose: () => void;
}

function quarterLabel(quarter: string): string {
  return `Q${quarter.slice(5)} ${quarter.slice(0, 4)}`;
}

function Outcome({ item }: { item: NudgeHistoryItem }) {
  if (item.difference === undefined) {
    return <span className="text-wg-06 text-xs">Outcome pending</span>;
  }
  const positive = item.difference >= 0;
  return (
    <span className={cn("text-xs font-semibold", positive ? "text-ok" : "text-risk")}>
      {positive ? "+" : ""}
      {item.difference}% pp after nudge
    </span>
  );
}

function NudgeEmailCard({ item }: { item: NudgeHistoryItem }) {
  return (
    <article className="border-wg-03 rounded-xl border bg-white p-4">
      <div className="mb-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Mail className="text-jj-red size-4 shrink-0" />
            <span className="text-[14px] font-semibold">{item.subject}</span>
            <Chip>{item.delivery === "automatic" ? "Auto-sent" : "Rep-approved"}</Chip>
            <ProvenanceBadge provenance="mock" />
          </div>
          <div className="text-wg-06 mt-1 text-xs">
            {item.sent_at ? formatShortDateTime(item.sent_at) : quarterLabel(item.quarter)}
          </div>
        </div>
        <Outcome item={item} />
      </div>

      <div className="border-wg-03 bg-wg-01 mb-3 rounded-lg border px-3 py-2 text-xs">
        <span className="text-wg-07">J&amp;J share: </span>
        <span className="font-semibold">{item.share_before}%</span>
        {item.share_final !== undefined && (
          <>
            <span className="text-wg-06"> → </span>
            <span className="font-semibold">{item.share_final}% at quarter close</span>
          </>
        )}
      </div>
      <p className="text-wg-07 text-[13px] leading-relaxed whitespace-pre-line">{item.body}</p>
    </article>
  );
}

export function NudgeHistoryDrawer({
  accountId,
  accountName,
  open,
  onClose,
}: NudgeHistoryDrawerProps) {
  const historyQuery = useShareNudges(open ? accountId : null);
  const history = historyQuery.data ?? [];

  return (
    <DrawerShell open={open} onClose={onClose} title="Sent nudge history" sub={accountName}>
      <div className="bg-wg-02 text-wg-07 mb-4 rounded-xl p-4 text-sm leading-relaxed">
        <div className="text-wg-08 mb-1 flex items-center gap-2 font-semibold">
          <TrendingUp className="text-jj-red size-4" /> Exact emails and observed share movement
        </div>
        Results show the account&apos;s share at the time of a nudge and at quarter close. They
        illustrate the demo journey; they do not establish that the email alone caused the change.
      </div>
      {historyQuery.isLoading ? (
        <Loader2 className="text-wg-05 mx-auto mt-8 size-5 animate-spin" />
      ) : historyQuery.isError ? (
        <div className="bg-wg-02 text-wg-06 rounded-xl p-5 text-center text-sm">
          Nudge history could not be loaded.
        </div>
      ) : history.length === 0 ? (
        <div className="bg-wg-02 text-wg-06 rounded-xl p-5 text-center text-sm">
          No nudge emails have been sent to this account yet.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {history.map((item) => (
            <NudgeEmailCard key={item.quarter} item={item} />
          ))}
        </div>
      )}
    </DrawerShell>
  );
}
